import { inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ScreenService } from '../screen/screen.service';

/** Breakpoint below which the sidebar auto-collapses on desktop. */
const COLLAPSE_BREAKPOINT = 1280;

/**
 * Service to manage sidebar state.
 */
@Injectable({
  providedIn: 'root'
})
export class SidebarService implements OnDestroy {
  private _screenService = inject(ScreenService);
  private isCollapsedSubject = new BehaviorSubject<boolean>(this.shouldAutoCollapse());
  isCollapsed$ = this.isCollapsedSubject.asObservable();

  private resizeListener = () => this.onResize();
  private lastAutoState: boolean;

  constructor() {
    this.lastAutoState = this.shouldAutoCollapse();
    window.addEventListener('resize', this.resizeListener);
  }

  ngOnDestroy() {
    window.removeEventListener('resize', this.resizeListener);
  }

  /** Whether the sidebar should auto-collapse based on current viewport. */
  private shouldAutoCollapse(): boolean {
    return window.innerWidth < COLLAPSE_BREAKPOINT;
  }

  /** Auto-collapse/expand when crossing the breakpoint. */
  private onResize(): void {
    const shouldCollapse = this.shouldAutoCollapse();
    if (shouldCollapse !== this.lastAutoState) {
      this.lastAutoState = shouldCollapse;
      this.isCollapsedSubject.next(shouldCollapse);
    }
  }

  /**
   * Toggles the sidebar collapsed state.
   */
  toggleSidebar() {
    this.isCollapsedSubject.next(!this.isCollapsedSubject.value);
  }

  /**
   * Gets the current collapsed state.
   * @returns {boolean} True if collapsed.
   */
  getIsCollapsed(): boolean {
    return this.isCollapsedSubject.value;
  }
}
